export interface InitialCashStorageContext {
  storeId: string;
  userId: string;
}

export interface InitialCashStorageRecord extends InitialCashStorageContext {
  amount: number | null;
  createdAt: string;
  skipped?: boolean;
}

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

export const canUseInitialCashStorage = () => getStorage() !== null;

export const buildInitialCashStorageKey = ({ storeId, userId }: InitialCashStorageContext) =>
  `washday:initialCash:${storeId}:${userId}`;

export const getInitialCashStorageRecord = (
  context: InitialCashStorageContext,
): InitialCashStorageRecord | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const rawValue = storage.getItem(buildInitialCashStorageKey(context));
    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue) as InitialCashStorageRecord;
    if (!parsedValue || typeof parsedValue !== 'object') return null;

    return parsedValue;
  } catch (error) {
    return null;
  }
};

export const getInitialCashAmount = (context: InitialCashStorageContext) => {
  const record = getInitialCashStorageRecord(context);
  return typeof record?.amount === 'number' ? record.amount : null;
};

const saveInitialCashRecord = (record: InitialCashStorageRecord) => {
  const storage = getStorage();
  if (!storage) return false;

  try {
    storage.setItem(buildInitialCashStorageKey(record), JSON.stringify(record));
    return true;
  } catch (error) {
    return false;
  }
};

export const saveInitialCashAmount = (
  context: InitialCashStorageContext,
  amount: number,
  createdAt: string = new Date().toISOString(),
) =>
  saveInitialCashRecord({
    ...context,
    amount,
    createdAt,
  });

export const saveInitialCashSkip = (
  context: InitialCashStorageContext,
  createdAt: string = new Date().toISOString(),
) =>
  saveInitialCashRecord({
    ...context,
    amount: null,
    skipped: true,
    createdAt,
  });

export const clearInitialCashStorageRecord = (context: InitialCashStorageContext) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(buildInitialCashStorageKey(context));
  } catch (error) {
    return;
  }
};
